/**
 * Put a document back to how it read at some heads, as a new change
 * (RFC 0313 3e).
 *
 * NOTHING IS REWOUND. Rewinding a document two people are editing means
 * throwing one of them away. A restore reads the content at the heads you
 * picked and writes it as the content now: text through `updateText`, so it
 * splices and a concurrent typist keeps their words, and an app record field
 * by field, writing only the fields that differ. The restore is one change
 * stamped `restores`, a row in history like any other, and can be undone.
 *
 * NEVER SILENTLY OVER NEWER WORK. The caller passes the heads the person was
 * looking at when they chose to restore. If the document has moved since, the
 * restore refuses and says how many changes arrived, and goes ahead only when
 * asked again with `force`.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { Automerge as A } from '@automerge/automerge-repo/slim'
import { changeNote } from '../session/agent-edits'
import { saveVersion } from '../versions/versions'
import { changeRow } from './history'
import { touchCatalog } from './mutations'
import { readDocumentContent } from './reads'
import {
  sessionsHolding,
  tellUndone,
  type UndoneBy,
  undoneSummary,
} from './told-undone'
import type { DocumentDoc } from './types'

export type RestoreResult =
  | { ok: true; hash: string | null }
  | { ok: false; reason: 'not-found' | 'unreadable' }
  /** The document moved after the person looked: `since` changes arrived. */
  | { ok: false; reason: 'moved'; since: number }

const sameHeads = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join()

/** Changes in `doc` that `seen` did not have. */
function changesSince(doc: A.Doc<unknown>, seen: string[]): number {
  try {
    return A.getChanges(A.view(doc, seen), doc).length
  } catch {
    return 0
  }
}

export async function restoreVersion(
  repo: Repo,
  projectId: string,
  documentId: string,
  heads: string[],
  opts: {
    /** Who is restoring, stamped on the change. */
    authorId?: string
    /** The heads the person was looking at; a later change refuses. */
    seenHeads?: string[]
    /** Restore over changes that arrived after `seenHeads`. */
    force?: boolean
    /** Who restored, for the events the overwritten sessions are told (3f). */
    by?: UndoneBy
    /**
     * Save the document as it is now as a version first, so the direction
     * being replaced is never lost (paired review of PR #329). On by default.
     */
    saveFirst?: boolean
  } = {},
): Promise<RestoreResult> {
  const handle = await repo.find<DocumentDoc>(documentId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const doc = handle.doc() as A.Doc<unknown> | undefined
  if (!doc) return { ok: false, reason: 'not-found' }

  if (opts.seenHeads && !opts.force) {
    const now = A.getHeads(doc)
    if (!sameHeads(now, opts.seenHeads)) {
      return {
        ok: false,
        reason: 'moved',
        since: changesSince(doc, opts.seenHeads),
      }
    }
  }

  if (opts.saveFirst !== false) {
    try {
      await saveVersion(repo, projectId, {
        name: 'Before restore',
        by: 'auto',
        covers: { kind: 'document', documentId },
      })
    } catch {
      // A project with no versions store yet still restores; the point is
      // a safety net, not a precondition.
    }
  }

  let then: unknown
  try {
    then = A.view(doc, heads)
  } catch {
    return { ok: false, reason: 'unreadable' }
  }
  const text = readDocumentContent(then)
  const hadText = typeof (then as { content?: unknown }).content !== 'undefined'
  const data = plain((then as { data?: unknown }).data)

  // The sessions whose changes this restore writes over: every session-stamped
  // change the document has that the restored heads did not.
  const overwritten = new Map<string, string>()
  try {
    for (const raw of A.getChanges(A.view(doc, heads), doc)) {
      const c = A.decodeChange(raw)
      const m = c.message?.startsWith('{') ? JSON.parse(c.message) : null
      if (m && typeof m.sessionId === 'string' && c.hash) {
        overwritten.set(m.sessionId, c.hash)
      }
    }
  } catch {
    // Nobody is told; the restore itself still goes ahead.
  }

  const now = Date.now()
  handle.change(
    (d) => {
      if (hadText) {
        if (typeof d.content !== 'string') d.content = ''
        A.updateText(d, ['content'], text)
      }
      const cur = d as unknown as { data?: Record<string, unknown> }
      if (data !== undefined) {
        if (!cur.data || typeof cur.data !== 'object') cur.data = {}
        applyInto(cur.data, data as Record<string, unknown>)
      } else if (cur.data !== undefined) {
        // It had no fields then, so it has none once restored.
        delete cur.data
      }
      d.updatedAt = now
    },
    {
      message: changeNote({
        restores: heads,
        ...(opts.authorId ? { authorId: opts.authorId } : {}),
      }),
    },
  )
  await touchCatalog(repo, projectId, documentId, now)
  const by = opts.by ?? { id: opts.authorId ?? 'user' }
  for (const [sessionId, hash] of overwritten) {
    const row = changeRow(doc, hash)
    await tellUndone(repo, [sessionId], {
      documentId,
      restores: heads,
      by,
      summary: row ? undoneSummary(row) : 'a change',
    })
  }
  // And every session holding the document, which may be about to write over
  // the restore (3f). One already told above is skipped there.
  await tellUndone(repo, await sessionsHolding(repo, projectId, documentId), {
    documentId,
    restores: heads,
    by,
    summary: 'restored the document to an earlier version',
  })
  const after = A.getHeads(handle.doc() as A.Doc<unknown>)
  return { ok: true, hash: after.length === 1 ? (after[0] ?? null) : null }
}

function plain(v: unknown): unknown {
  if (v === undefined) return undefined
  return JSON.parse(JSON.stringify(v))
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v)

/**
 * Make `target` read as `source`, touching only what differs: a nested object
 * is walked, anything else (a value, a list) is written whole when it differs.
 */
function applyInto(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
) {
  for (const k of Object.keys(target)) {
    if (!(k in source)) delete target[k]
  }
  for (const [k, v] of Object.entries(source)) {
    const cur = target[k]
    if (isRecord(v) && isRecord(cur)) {
      applyInto(cur, v)
    } else if (JSON.stringify(plain(cur)) !== JSON.stringify(v)) {
      target[k] = v
    }
  }
}
